import React, { useEffect, useState } from "react";
import { ArrowRight, ExternalLink, PackageCheck, RefreshCw, Truck } from "lucide-react";
import { Link, useLocation, useOutletContext } from "react-router-dom";
import { getErrorMessage, formatCurrency } from "../core/api.jsx";
import {
  cancelMyOrder,
  getMyOrders,
  requestMyOrderReturn,
} from "../core/services/orderService";
import { useAuth } from "../contexts/AuthContext";

const statusClass = (status = "") =>
  status.toLowerCase().replace(/[^a-z]+/g, "-");
export default function Orders() {
  const { onLogin } = useOutletContext();
  const { token } = useAuth();
  const { state } = useLocation();
  const [orders, setOrders] = useState(
    state?.placedOrder ? [state.placedOrder] : [],
  );
  const [loading, setLoading] = useState(Boolean(token));
  const [error, setError] = useState("");
  const [orderAction, setOrderAction] = useState(null);
  const [actionReason, setActionReason] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionBusy, setActionBusy] = useState(false);
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return undefined;
    }
    let alive = true;
    getMyOrders()
      .then((data) => {
        if (alive) setOrders(data.orders || []);
      })
      .catch((requestError) => {
        if (alive) setError(getErrorMessage(requestError));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [token]);

  const openOrderAction = (type, order) => {
    setOrderAction({ type, order });
    setActionReason("");
    setActionError("");
  };

  const submitOrderAction = async (event) => {
    event.preventDefault();
    const reason = actionReason.trim();
    if (reason.length < 3) {
      setActionError("Please enter a reason with at least 3 characters.");
      return;
    }
    setActionBusy(true);
    setActionError("");
    try {
      const request = orderAction.type === "cancel"
        ? cancelMyOrder(orderAction.order._id, reason)
        : requestMyOrderReturn(orderAction.order._id, reason);
      const data = await request;
      setOrders((current) =>
        current.map((order) =>
          order._id === data.order?._id ? data.order : order,
        ),
      );
      setOrderAction(null);
      setActionReason("");
    } catch (requestError) {
      setActionError(getErrorMessage(requestError));
    } finally {
      setActionBusy(false);
    }
  };
  if (!token)
    return (
      <main className="page-shell">
        <div className="empty-state">
          <span className="empty-state__icon">
            <PackageCheck />
          </span>
          <h2>Your orders live here.</h2>
          <p>Sign in to see order details and delivery updates.</p>
          <button className="button button--primary" onClick={onLogin}>
            Sign in <ArrowRight size={17} />
          </button>
        </div>
      </main>
    );
  return (
    <main className="page-shell content-page">
      <div className="breadcrumbs">
        <Link to="/">Home</Link>
        <span>/</span>
        <span>My orders</span>
      </div>
      <div className="page-title-row">
        <div>
          <p className="eyebrow">Every good thing, tracked</p>
          <h1>Your orders</h1>
        </div>
        <PackageCheck size={25} />
      </div>
      {loading && <div className="loading-note">Loading your orders…</div>}
      {error && (
        <div className="inline-alert">
          <span>{error}</span>
          <button
            className="icon-button"
            onClick={() => window.location.reload()}
          >
            <RefreshCw size={16} />
          </button>
        </div>
      )}
      {!loading && !error && orders.length === 0 && (
        <div className="empty-state">
          <span className="empty-state__icon">
            <PackageCheck />
          </span>
          <h2>No orders yet.</h2>
          <p>When you find your next favourite, it’ll show up here.</p>
          <Link className="button button--primary" to="/shop">
            Explore the shop <ArrowRight size={17} />
          </Link>
        </div>
      )}
      <div className="orders-list">
        {orders.map((order) => (
          <article className="order-card" key={order._id}>
            <header>
              <div>
                <span className="eyebrow">
                  Order{" "}
                  {order.orderCode || String(order._id).slice(-8).toUpperCase()}
                </span>
                <p>
                  Placed{" "}
                  {new Date(order.createdAt).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </p>
              </div>
              <span
                className={`status-pill status-pill--${statusClass(order.orderStatus)}`}
              >
                {order.orderStatus || "Processing"}
              </span>
            </header>
            <div className="order-card__items">
              {(order.orderItems || []).map((item, index) => (
                <div
                  className="order-card__item"
                  key={item._id || `${item.product}-${index}`}
                >
                  <span>
                    {item.name || item.product?.name || "Store item"}{" "}
                    {item.variantName && <em>· {item.variantName} </em>}
                    <em>× {item.quantity}</em>
                  </span>
                  <strong>{formatCurrency(item.price * item.quantity)}</strong>
                </div>
              ))}
            </div>
            <section className="order-tracking" aria-label="Delivery tracking">
              <div className="order-tracking__heading">
                <Truck size={17} />
                <strong>Delivery tracking</strong>
              </div>
              {order.trackingInfo?.trackingNumber || order.trackingInfo?.courierName ? (
                <div className="order-tracking__details">
                  {order.trackingInfo.courierName && <span>{order.trackingInfo.courierName}</span>}
                  {order.trackingInfo.trackingNumber && (
                    <span>Tracking number: <strong>{order.trackingInfo.trackingNumber}</strong></span>
                  )}
                  {order.trackingInfo.estimatedDelivery && (
                    <span>Estimated delivery: {new Date(order.trackingInfo.estimatedDelivery).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                  )}
                  {order.trackingInfo.note && <span>{order.trackingInfo.note}</span>}
                  {/^https?:\/\//i.test(order.trackingInfo.trackingUrl || "") && (
                    <a href={order.trackingInfo.trackingUrl} target="_blank" rel="noreferrer">
                      Track with courier <ExternalLink size={14} />
                    </a>
                  )}
                </div>
              ) : (
                <p>Tracking details will appear here once your order ships.</p>
              )}
              {order.returnRequest?.status && order.returnRequest.status !== "None" && (
                <p className="order-tracking__return-status">
                  Return request: {order.returnRequest.status}
                  {order.returnRequest.adminNote ? ` · ${order.returnRequest.adminNote}` : ""}
                </p>
              )}
            </section>
            <footer>
              <span>
                {order.paymentInfo?.method === "cod"
                  ? "Cash on delivery"
                  : "Paid online"}
              </span>
              <strong>Total · {formatCurrency(order.totalPrice)}</strong>
            </footer>
            <div className="order-card__actions">
              {order.orderStatus === "Processing" && (
                <button className="button button--quiet" onClick={() => openOrderAction("cancel", order)}>
                  Cancel order
                </button>
              )}
              {order.orderStatus === "Delivered" && (
                order.returnRequest?.status === "Requested" ? (
                  <span className="order-action-note">Return requested</span>
                ) : order.returnRequest?.status === "Approved" ? (
                  <span className="order-action-note">Return approved</span>
                ) : order.returnRequest?.status === "Rejected" ? (
                  <span className="order-action-note">Return request rejected</span>
                ) : (
                  <button className="button button--quiet" onClick={() => openOrderAction("return", order)}>
                    Request a return
                  </button>
                )
              )}
            </div>
          </article>
        ))}
      </div>
      {orderAction && (
        <div className="order-action-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget && !actionBusy) setOrderAction(null);
        }}>
          <form className="order-action-dialog" role="dialog" aria-modal="true" aria-labelledby="order-action-title" onSubmit={submitOrderAction}>
            <p className="eyebrow">Order {orderAction.order.orderCode || "request"}</p>
            <h2 id="order-action-title">{orderAction.type === "cancel" ? "Cancel this order?" : "Request a return"}</h2>
            <p>{orderAction.type === "cancel" ? "This order can be cancelled before it ships. Add a short reason to continue." : "Tell us why you would like to return this order. Return eligibility will be checked when submitted."}</p>
            <label className="field-label" htmlFor="order-action-reason">Reason</label>
            <textarea id="order-action-reason" className="text-field order-action-dialog__reason" value={actionReason} onChange={(event) => setActionReason(event.target.value)} maxLength={500} rows={4} required />
            {actionError && <p className="form-error">{actionError}</p>}
            <div className="order-action-dialog__buttons">
              <button type="button" className="button button--quiet" disabled={actionBusy} onClick={() => setOrderAction(null)}>Keep order</button>
              <button className="button button--primary" disabled={actionBusy}>{actionBusy ? "Sending…" : orderAction.type === "cancel" ? "Confirm cancellation" : "Submit return request"}</button>
            </div>
          </form>
        </div>
      )}
    </main>
  );
}
